import { PropsWithChildren, createContext, useContext, useState } from "react";
import { IProductOnline } from "../types/types";

export interface ICartContext {
  items: IProductOnline[];
  itemTotal: number;
  addItem: (item: IProductOnline) => void;
  removeItem: (item: IProductOnline) => void;
  clearCart: () => void;
}

const CartContext = createContext<ICartContext>({
  items: [],
  itemTotal: 0,
  addItem: () => {},
  removeItem: () => {},
  clearCart: () => {},
});

export const useCartContext = () => useContext(CartContext);

/**
 * Holds the cart so it survives navigating from the menu to checkout. The cart
 * is in memory only: a refresh on checkout empties it and the page sends the
 * customer back to the menu.
 */
export default function CartProvider(props: PropsWithChildren) {
  const [items, setItems] = useState<IProductOnline[]>([]);

  const addItem = (item: IProductOnline) =>
    setItems((existing) => [...existing, item]);

  // The same product can be added more than once, so drop one entry, not all.
  const removeItem = (item: IProductOnline) =>
    setItems((existing) => {
      const next = [...existing];
      const index = next.findIndex((each) => each._id === item._id);
      if (index > -1) {
        next.splice(index, 1);
      }
      return next;
    });

  const clearCart = () => setItems([]);

  const itemTotal = items.reduce(
    (acc, item) => acc + (item.price?.value ?? 0),
    0,
  );

  return (
    <CartContext.Provider
      value={{ items, itemTotal, addItem, removeItem, clearCart }}
    >
      {props.children}
    </CartContext.Provider>
  );
}
